'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var EventEmitter = _interopDefault(require('eventemitter3'));
var React = require('react');
var React__default = _interopDefault(React);
var typesafeActions = require('typesafe-actions');
var hoistNonReactStatics = _interopDefault(require('hoist-non-react-statics'));

/**
 * Fetch policies which will return the cached value (if exists)
 */
const READ_CACHE_POLICIES = [
    'cache-first',
    'cache-only',
    'cache-and-fetch'
];
const DEFAULT_FETCH_POLICY = 'fetch-first';
const DEFAULT_REQUEST_METHOD = 'GET';

/**
 * Thrown if a value doesn't exist in the cache
 */
class ApiCacheMissError extends Error {
}
/**
 * Thrown if a non-200 status response is received
 */
class ApiError extends Error {
    constructor(status, responseBody, responseType) {
        super(`API Error: ${status}`);
        this.responseBody = responseBody;
        this.status = status;
        this.responseType = responseType;
    }
}

class GenericCache {
    constructor() {
        this.valuesByCacheKey = new Map();
    }
    has(key) {
        return this.valuesByCacheKey.has(key);
    }
    get(key) {
        return this.valuesByCacheKey.get(key) || null;
    }
    set(key, value) {
        this.valuesByCacheKey.set(key, value);
    }
    del(key) {
        this.valuesByCacheKey.delete(key);
    }
}

/**
 * Returns an id represent the request parameters.
 *
 * This is used to identify the request for caching and other purposes.
 */
function getParamsId(params) {
    return JSON.stringify([
        params.method || DEFAULT_REQUEST_METHOD,
        params.url,
        params.responseType || '',
        serializeHeaders(params.headers),
        params.extraKey
    ]);
}
function serializeHeaders(paramHeaders) {
    if (!paramHeaders) {
        return '';
    }
    const headers = applyHeaders(new Headers(), paramHeaders);
    const headerPairs = [];
    headers.forEach((value, key) => {
        headerPairs.push(`${key}:${value};`);
    });
    headerPairs.sort();
    return headerPairs.join('');
}
/**
 * Given a fetch `Headers` object and ApiHeaders (object or array of key-value pairs),
 * returns a new `Headers` object with the ApiHeaders added to the original `Headers`.
 */
function applyHeaders(prevHeaders, apiHeaders) {
    const headers = new Headers(prevHeaders);
    for (const key of Object.keys(apiHeaders)) {
        headers.set(key, apiHeaders[key]);
    }
    return headers;
}
function genCacheUpdateEvent(params) {
    return `cacheUpdate.${getParamsId(params)}`;
}
/**
 * Adds an event listener to the given emitter using the provided event key.
 *
 * Returns an "unsubscribe" function that will remove the listener once called.
 *
 * @param emitter Event emitter to subscribe to
 * @param event Event key
 * @param listener Event listener
 */
function createSubscription(emitter, event, listener) {
    emitter.on(event, listener);
    return () => {
        emitter.off(event, listener);
    };
}

var __awaiter = (undefined && undefined.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * Is called by the `RequestManager` to make the request over the network.
 *
 * Responsible for:
 * - Throwing ApiErrors for non-200 level errors
 * - Parsing response bodies
 */
class ApiRequestFetcher {
    constructor() {
        this.getResponse = (params) => __awaiter(this, void 0, void 0, function* () {
            const request = this.createRequest(params);
            const response = yield fetch(request);
            const { responseBody, responseType } = yield this.parseResponseBody(response, params);
            if (!response.ok) {
                throw new ApiError(response.status, responseBody, responseType);
            }
            return { responseBody, responseType };
        });
    }
    /**
     * Generates a `Request` instance to be passed directly to `fetch`
     */
    createRequest(params) {
        return new Request(params.url, {
            method: params.method,
            body: params.body,
            headers: params.headers
        });
    }
    /**
     * Parses response body from `fetch`'s `Response`
     */
    parseResponseBody(response, params) {
        return __awaiter(this, void 0, void 0, function* () {
            const responseType = params.responseType ||
                this.inferResponseTypeUsingContentType(response.headers.get('Content-Type'));
            if (!responseType) {
                return { responseBody: null, responseType: null };
            }
            const responseBody = yield (() => {
                switch (responseType) {
                    case 'json':
                        return response.json();
                    case 'text':
                        return response.text();
                    case 'blob':
                        return response.blob();
                    default:
                        throw new TypeError(`'${responseType}' is not a valid response type`);
                }
            })();
            return { responseBody, responseType };
        });
    }
    /**
     * Attempts to infer the response type for parsing using the
     * 'Content-Type' header
     */
    inferResponseTypeUsingContentType(contentType) {
        if (!contentType) {
            return null;
        }
        if (contentType === 'application/json') {
            return 'json';
        }
        if (['application/', 'image/', 'video/'].some((prefix) => contentType.startsWith(prefix))) {
            return 'blob';
        }
        if (contentType.startsWith('text/')) {
            return 'text';
        }
        return null;
    }
}

var __awaiter$1 = (undefined && undefined.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
function identity(value) {
    return value;
}
const RECEIVED_RESPONSE_BODY_EVENT = 'received_response_body';
const ERROR_EVENT = 'error';
/**
 * The request manager is responsible for managing API requests.
 *
 * Responsibilities include:
 * - Implementing `getRequestBody` which is called by `Api`
 * - Caching in-progress GET requests
 * - Detecting a JSON request body by stringifying and setting the appropriate Content-Type headers
 * - Serializing / parsing JSON requests & responses
 */
class ApiRequestManager {
    constructor(params) {
        this.emitter = new EventEmitter();
        this.defaultHeaders = new Headers();
        this.inProgressRequestCache = new GenericCache();
        /**
         * Returns a promise that resolves with the response body.
         *
         * If there is an in-progress request, and `options.deduplicate` is `true`,
         * it will return the same promise.
         *
         * On a successful fetch, it will cache the response unless `fetchPolicy` is `'no-cache'`
         */
        this.getResponseBody = (params, options) => {
            var _a;
            const paramsKey = getParamsId(params);
            // return cached promise if it exists
            const cachedRequest = this.inProgressRequestCache.get(paramsKey);
            const deduplicate = (_a = options.deduplicate, (_a !== null && _a !== void 0 ? _a : defaultDeduplicate(params)));
            if (cachedRequest && deduplicate) {
                // if user hasn't explicitly requested a new fetch,
                // return the in-progress fetch promise
                return cachedRequest.requestPromise;
            }
            const id = Symbol();
            const fetchPromise = this.createRequestPromise(params, options, id);
            this.inProgressRequestCache.set(paramsKey, {
                id,
                requestPromise: fetchPromise
            });
            return fetchPromise;
        };
        /**
         * Configuring an error handler to be called on error
         */
        this.onReceivedResponseBody = (listener) => {
            return createSubscription(this.emitter, RECEIVED_RESPONSE_BODY_EVENT, listener);
        };
        /**
         * Configuring an error handler to be called on error
         */
        this.onError = (listener) => {
            return createSubscription(this.emitter, ERROR_EVENT, listener);
        };
        /**
         * Returns `true` if a `GET` request matches params
         */
        this.requestInProgress = (params) => {
            return this.inProgressRequestCache.has(getParamsId(params));
        };
        /**
         * Set default headers to be passed to all API requests.
         * Useful for setting an authentication token.
         *
         * @param key Header key
         * @param value Header value
         */
        this.setDefaultHeader = (key, value) => {
            this.defaultHeaders.set(key, value);
        };
        this.fetchResponseBody = (params, options) => __awaiter$1(this, void 0, void 0, function* () {
            const { method = DEFAULT_REQUEST_METHOD } = params;
            const { fetchPolicy = DEFAULT_FETCH_POLICY } = options;
            try {
                let headers = new Headers(this.defaultHeaders);
                if (params.headers) {
                    headers = applyHeaders(headers, params.headers);
                }
                let body;
                if (['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
                    const paramBody = params.body;
                    if (paramBody &&
                        ![Blob, FormData, URLSearchParams, ReadableStream].some((bodyType) => paramBody instanceof bodyType) &&
                        typeof paramBody !== 'string') {
                        // prepare JSON body and add header
                        body = JSON.stringify(this.serializeRequestJson(paramBody));
                        headers.set('Content-Type', 'application/json');
                    }
                    else {
                        body = paramBody;
                    }
                }
                const response = yield this.requestFetcher.getResponse({
                    method,
                    url: `${this.baseUrl}${params.url}`,
                    body,
                    headers,
                    responseType: params.responseType
                });
                const responseBody = response.responseType === 'json'
                    ? this.parseResponseJson(response.responseBody)
                    : response.responseBody;
                if (fetchPolicy !== 'no-cache') {
                    this.emitter.emit(RECEIVED_RESPONSE_BODY_EVENT, params, responseBody);
                }
                return responseBody;
            }
            catch (error) {
                /* istanbul ignore next */
                if (error instanceof ApiError && error.responseType === 'json') {
                    error.responseBody = this.parseResponseJson(error.responseBody);
                }
                this.emitter.emit(ERROR_EVENT, error);
                throw error;
            }
        });
        this.baseUrl = params.baseUrl || '';
        this.requestFetcher = params.requestFetcher || new ApiRequestFetcher();
        this.serializeRequestJson = params.serializeRequestJson || identity;
        this.parseResponseJson = params.parseResponseJson || identity;
    }
    createRequestPromise(params, options, id) {
        return __awaiter$1(this, void 0, void 0, function* () {
            const paramsId = getParamsId(params);
            try {
                return yield this.fetchResponseBody(params, options);
            }
            finally {
                const cachedRequest = this.inProgressRequestCache.get(paramsId);
                if (cachedRequest && cachedRequest.id === id) {
                    this.inProgressRequestCache.del(paramsId);
                }
            }
        });
    }
}
function defaultDeduplicate(params) {
    return params.method === 'GET' ? true : false;
}

const ERROR_EVENT$1 = 'error';
class Api {
    constructor(params = {}) {
        this.responseBodyCache = new GenericCache();
        this.emitter = new EventEmitter();
        /**
         * Makes an API request
         */
        this.request = (params, options = {}) => {
            const { fetchPolicy = DEFAULT_FETCH_POLICY } = options;
            if (!READ_CACHE_POLICIES.includes(fetchPolicy)) {
                return this.requestManager.getResponseBody(params, options);
            }
            const cachedResponse = this.responseBodyCache.get(getParamsId(params));
            if (cachedResponse) {
                if (fetchPolicy === 'cache-and-fetch') {
                    // kick off in the background
                    // once it succeeds, the cache will be updated
                    // and subscribers will be notified
                    this.requestManager.getResponseBody(params, options).catch(() => {
                        // ignore - can be handled via `Api#onError`
                    });
                }
                return Promise.resolve(cachedResponse);
            }
            if (fetchPolicy === 'cache-only') {
                return Promise.reject(new ApiCacheMissError(`Cache miss: ${params.url}`));
            }
            return this.requestManager.getResponseBody(params, options);
        };
        /**
         * Returns `true` if a `GET` of `DELETE` request matches params
         */
        this.requestInProgress = (params) => {
            return this.requestManager.requestInProgress(params);
        };
        /**
         * Saves a response directly to the cache
         */
        this.writeCachedResponse = (params, responseBody) => {
            this.responseBodyCache.set(getParamsId(params), responseBody);
            this.emitter.emit(genCacheUpdateEvent(params), responseBody);
        };
        /**
         * Reads a response directly from the cache
         *
         * The main reason you would use this over `Api#request` with a cached
         * fetch policy is that this runs synchronously.
         *
         * Note that if there is a cache miss, it will return `undefined`
         */
        this.readCachedResponse = (params) => {
            return this.responseBodyCache.get(getParamsId(params));
        };
        /**
         * Subscribes to cache updates for a given param's response
         */
        this.onCacheUpdate = (params, listener) => {
            const event = genCacheUpdateEvent(params);
            return createSubscription(this.emitter, event, listener);
        };
        /**
         * Returns the base url concatenated with the provided path
         */
        this.buildUrl = (path) => {
            return `${this.requestManager.baseUrl}${path}`;
        };
        /**
         * Set default headers to be passed to all API requests.
         * Useful for setting an authentication token.
         *
         * @param key Header key
         * @param value Header value
         */
        this.setDefaultHeader = (key, value) => {
            this.requestManager.setDefaultHeader(key, value);
        };
        /**
         * Configuring an error handler to be called on error
         */
        this.onError = (listener) => {
            return createSubscription(this.emitter, ERROR_EVENT$1, listener);
        };
        this.requestManager = new ApiRequestManager(params);
        this.defaultFetchPolicy = params.defaultFetchPolicy || DEFAULT_FETCH_POLICY;
        this.requestManager.onReceivedResponseBody(this.writeCachedResponse);
        this.requestManager.onError((error) => this.emitter.emit(ERROR_EVENT$1, error));
    }
}

const DEFAULT_FETCH_POLICY$1 = 'cache-and-fetch';
const ApiContext = React.createContext({
    api: new Api(),
    defaultFetchPolicy: DEFAULT_FETCH_POLICY$1
});
const ApiProvider = function ApiProvider(props) {
    return (React__default.createElement(ApiContext.Provider, { value: {
            api: props.api,
            defaultFetchPolicy: props.defaultFetchPolicy || DEFAULT_FETCH_POLICY$1
        } }, props.children));
};

function useApi() {
    return React.useContext(ApiContext).api;
}

const useApiQueryActions = {
    /**
     * Resets to the default state.
     *
     * This is useful if no request params are passed to the hook.
     */
    reset: typesafeActions.createStandardAction('RESET')(),
    /**
     * A request has begun.
     */
    request: typesafeActions.createStandardAction('REQUEST')(),
    /**
     * A refetchRequest has begun.
     */
    refetchRequest: typesafeActions.createStandardAction('REFETCH_REQUEST')(),
    /**
     * A request (or refetchRequest) has completed.
     */
    success: typesafeActions.createStandardAction('SUCCESS')(),
    /**
     * A request (or refetchRequest) has failed.
     */
    failure: typesafeActions.createStandardAction('FAILURE')(),
    /**
     * The `data` should be set manually.
     */
    setData: typesafeActions.createStandardAction('SET_DATA')()
};

const INITIAL_STATE = {
    id: null,
    paramsId: null,
    loading: false,
    data: null,
    error: null
};
const useApiQueryReducer = (prev = INITIAL_STATE, action) => {
    switch (action.type) {
        case typesafeActions.getType(useApiQueryActions.reset):
            return INITIAL_STATE;
        case typesafeActions.getType(useApiQueryActions.request):
            return Object.assign(Object.assign({}, prev), { id: action.payload.id, paramsId: action.payload.paramsId, loading: true, data: action.payload.initData ||
                    (action.payload.dontReinitialize ? prev.data : null) });
        case typesafeActions.getType(useApiQueryActions.success):
            if (isLiveRequest(prev, action)) {
                return Object.assign(Object.assign({}, prev), { loading: false, data: action.payload.data, error: null });
            }
            return prev;
        case typesafeActions.getType(useApiQueryActions.failure):
            if (isLiveRequest(prev, action)) {
                return Object.assign(Object.assign({}, prev), { loading: false, data: null, error: action.payload.error });
            }
            return prev;
        case typesafeActions.getType(useApiQueryActions.refetchRequest):
            // ensure that the refetch has the correct request params
            if (action.payload.paramsId === prev.paramsId) {
                return Object.assign(Object.assign({}, prev), { id: action.payload.id, paramsId: action.payload.paramsId, loading: true, data: action.payload.reinitialize ? null : prev.data });
            }
            return prev;
        case typesafeActions.getType(useApiQueryActions.setData):
            return Object.assign(Object.assign({}, prev), { data: action.payload });
        default:
            return prev;
    }
};
/**
 * Ensures that the completed request was the last one that was called
 */
function isLiveRequest(prev, action) {
    return (action.payload.id === prev.id && action.payload.paramsId === prev.paramsId);
}

var __awaiter$2 = (undefined && undefined.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * API hook to run an api `GET` request, returning a `Loadable`
 * instance of the response data
 */
function useApiQuery(params, opts = {}) {
    const { api, defaultFetchPolicy } = React.useContext(ApiContext);
    const fetchPolicy = opts.fetchPolicy || defaultFetchPolicy;
    const paramsId = params && getParamsId(params);
    const [state, dispatch] = React.useReducer(useApiQueryReducer, null, () => ({
        id: null,
        paramsId: null,
        loading: !!params,
        data: 
        // if this api request will read from the cache,
        // initialize data to the value in the cache
        params && READ_CACHE_POLICIES.includes(fetchPolicy)
            ? api.readCachedResponse(params) || null
            : null,
        error: null
    }));
    React.useEffect(() => {
        if (!params) {
            dispatch(useApiQueryActions.reset());
            return undefined;
        }
        const unsubscribe = api.onCacheUpdate(params, (responseBody) => {
            dispatch(useApiQueryActions.setData(responseBody));
        });
        const cachedData = READ_CACHE_POLICIES.includes(fetchPolicy)
            ? api.readCachedResponse(params)
            : null;
        if (fetchPolicy === 'cache-only' && cachedData) {
            // don't bother kicking off a request
            // since we already have the cached data
            useApiQueryActions.setData(cachedData);
            return undefined;
        }
        const id = Symbol();
        dispatch(useApiQueryActions.request({
            id,
            paramsId: paramsId,
            dontReinitialize: opts.dontReinitialize,
            initData: cachedData || null
        }));
        (() => __awaiter$2(this, void 0, void 0, function* () {
            try {
                const data = yield api.request(params, {
                    fetchPolicy,
                    deduplicate: opts.deduplicate
                });
                dispatch(useApiQueryActions.success({
                    id,
                    paramsId: paramsId,
                    data
                }));
            }
            catch (error) {
                dispatch(useApiQueryActions.failure({
                    id,
                    paramsId: paramsId,
                    error
                }));
            }
        }))();
        return () => {
            unsubscribe();
        };
    }, [paramsId]);
    // return loading flag if `useEffect` hasn't kicked in yet
    // but the a new request is about to kick off
    const prevParamsId = usePrevious(paramsId);
    const aboutToStartNewRequest = paramsId && paramsId !== prevParamsId;
    const returnedState = React.useMemo(() => ({
        loading: aboutToStartNewRequest || state.loading,
        data: state.data,
        error: state.error
    }), [aboutToStartNewRequest, state.data, state.loading, state.error]);
    return [
        returnedState,
        {
            setData: (data) => {
                if (fetchPolicy === 'no-cache' || !params) {
                    dispatch(useApiQueryActions.setData(data));
                }
                else {
                    // write to the cache, which will in turn
                    // notify the subscription and update `state.data`
                    api.writeCachedResponse(params, data);
                }
            },
            refetch: (refetchOpts = {}) => __awaiter$2(this, void 0, void 0, function* () {
                var _a;
                if (!paramsId || !params) {
                    return;
                }
                const id = Symbol();
                dispatch(useApiQueryActions.refetchRequest({
                    id,
                    paramsId,
                    reinitialize: refetchOpts.reinitialize
                }));
                try {
                    const data = yield api.request(params, {
                        fetchPolicy: fetchPolicy === 'no-cache' ? 'no-cache' : 'fetch-first',
                        deduplicate: (_a = refetchOpts.deduplicate, (_a !== null && _a !== void 0 ? _a : opts.deduplicate))
                    });
                    dispatch(useApiQueryActions.success({ id, paramsId, data }));
                }
                catch (error) {
                    dispatch(useApiQueryActions.failure({ id, paramsId, error }));
                }
            })
        }
    ];
}
/**
 * Returns previous value, or null if first render pass
 * @param value updating value
 */
function usePrevious(value) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}

function withApi(BaseComponent) {
    const WithApi = (props) => {
        const api = useApi();
        return React__default.createElement(BaseComponent, Object.assign({}, props, { api: api }));
    };
    hoistNonReactStatics(WithApi, BaseComponent);
    WithApi.displayName = `WithApi(${getDisplayName(BaseComponent)})`;
    return WithApi;
}
function getDisplayName(Component) {
    return Component.displayName || Component.name || 'Component';
}

/* eslint-disable no-dupe-class-members */
/**
 * `HttpEndpoints` provides static utilities for implementing a set of
 * API endpoints which returns requests that can be passed to `api.request`.
 */
class HttpEndpoints {
    /**
     * Returns a new path by appending a given `relativePath` to `this.basePath`.
     */
    static buildPath(relativePath) {
        let builtPath = `${this.basePath}${relativePath}`;
        if (this.trailingSlash && builtPath[builtPath.length - 1] !== '/') {
            builtPath += '/';
        }
        return builtPath;
    }
    /**
     * Creates a `GET` request.
     *
     * @param path A path relative to the base path.
     * @param requestInit Additional request parameters.
     */
    static _get(path, requestInit) {
        return this._createRequest('GET', path, requestInit);
    }
    /**
     * Creates a `POST` request.
     *
     * @param path A path relative to the base path.
     * @param requestInit Additional request parameters.
     */
    static _post(path, requestInit) {
        return this._createRequest('POST', path, requestInit);
    }
    /**
     * Creates a `PUT` request.
     *
     * @param path A path relative to the base path.
     * @param requestInit Additional request parameters.
     */
    static _put(path, requestInit) {
        return this._createRequest('PUT', path, requestInit);
    }
    /**
     * Creates a `PATCH` request.
     *
     * @param path A path relative to the base path.
     * @param requestInit Additional request parameters.
     */
    static _patch(path, requestInit) {
        return this._createRequest('PATCH', path, requestInit);
    }
    /**
     * Creates a `DELETE` request.
     *
     * @param path A path relative to the base path.
     * @param requestInit Additional request parameters.
     */
    static _delete(path, requestInit) {
        return this._createRequest('DELETE', path, requestInit);
    }
    /**
     * Given an object of key-value pairs, will serialize the query parameters into a search string.
     * This is called internally when serializing the query parameters into the request `url`.
     *
     * To customize query parameter serialization, override this in your base class.
     */
    static _serializeQuery(query) {
        const queryWithoutEmptyValues = Object.entries(query).reduce((prev, [key, value]) => {
            if (value !== undefined) {
                prev[key] = value;
            }
            return prev;
        }, {});
        const params = new URLSearchParams(queryWithoutEmptyValues);
        params.sort();
        return params.toString();
    }
    static _createRequest(method, path, requestInit = {}) {
        let url = this.buildPath(path);
        if (requestInit.query) {
            url += `?${this._serializeQuery(requestInit.query)}`;
        }
        return {
            method,
            url,
            headers: requestInit.headers,
            body: requestInit.body,
            extraKey: requestInit.extraKey
        };
    }
}
/**
 * The base path to prefix all underlying requests.
 */
HttpEndpoints.basePath = '';
/**
 * Some REST APIs require that requests end with a trailing slash. (ie. `/users/` instead of `/users`)
 * If `true`, will ensure that a trailing `/` is added to each request.
 */
HttpEndpoints.trailingSlash = false;
class RestEndpoints extends HttpEndpoints {
    static _singlePath(id) {
        return dynamicPath('/:id', { id });
    }
    /**
     * Returns the path for a given resource id, as a convenience.
     */
    static pathToResource(id) {
        return super.buildPath(this._singlePath(id));
    }
    /**
     * Performs a `GET` at `/`, appending an optional `query`
     * @param query Query parameters
     */
    static _list(query) {
        return super._get(this._collectionPath, { query });
    }
    /**
     * Performs a `POST` at `/`, using an optional request `body`
     */
    static _create(body) {
        return super._post(this._collectionPath, { body });
    }
    /**
     * Performs a `GET` at `/:id`
     */
    static _findById(id) {
        return super._get(this._singlePath(id));
    }
    /**
     * Performs a `PUT` at `/:id`, using an optional request `body`
     */
    static _update(id, body) {
        return super._put(this._singlePath(id), { body });
    }
    /**
     * Performs a `PATCH` at `/:id`, using an optional request `body`
     */
    static _partialUpdate(id, body) {
        return super._patch(this._singlePath(id), { body });
    }
    /**
     * Performs a `DELETE` at `/:id`, using an optional request `body`
     */
    static _destroy(id, body) {
        return super._delete(this._singlePath(id), { body });
    }
}
RestEndpoints._collectionPath = '';
/**
 * Applies colon-delimited path params to a given path pattern
 *
 * @param pattern Path pattern
 * @param params Path params
 *
 * @example `buildPath('/users/:id', { id: 4 })` => `/users/4`
 */
function dynamicPath(pattern, params) {
    return Object.entries(params).reduce((prev, [paramKey, paramValue]) => {
        return prev.replace(`:${paramKey}`, encodeURIComponent(String(paramValue)));
    }, pattern);
}

exports.Api = Api;
exports.ApiCacheMissError = ApiCacheMissError;
exports.ApiContext = ApiContext;
exports.ApiError = ApiError;
exports.ApiProvider = ApiProvider;
exports.HttpEndpoints = HttpEndpoints;
exports.RestEndpoints = RestEndpoints;
exports.dynamicPath = dynamicPath;
exports.useApi = useApi;
exports.useApiQuery = useApiQuery;
exports.withApi = withApi;
//# sourceMappingURL=index.cjs.js.map
